import { RiSaveLine } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useRouter } from "next/router";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useSWRConfig } from "swr";
import { useDirectUploadStatus } from "~/components/common/deals/new/DirectUploadStatusContext";
import useLoader from "~/hooks/useLoader";
import { propertyDealCacheKey } from "~/hooks/usePropertyDeals";
import type { Deal } from "~/models/Deal";
import type { RelatedDealParam } from "~/models/DealForm";
import { normalizeFields, shouldConfirmShowTenant } from "~/models/DealForm";
import { PATH } from "~/pages/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { create } from "~/utils/api/deals/RelatedDeal";
import { onInvalidScroll } from "~/utils/FormUtil";
import Styles from "../../../../../common/deals/new/DealEditorFooter.module.scss";
import { ShowTenantConfirmModal } from "../../../../../common/deals/new/ShowTenantConfirmModal";

type Props = {
	deal: Deal;
};

export function DealEditorFooter({ deal }: Props) {
	const { notify } = useNotification();
	const router = useRouter();
	const { cache } = useSWRConfig();
	const { getValues, handleSubmit } = useFormContext<RelatedDealParam>();
	const { Loader, showLoader, hideLoader } = useLoader();
	const { isUploading } = useDirectUploadStatus();
	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);

	const executeSave = React.useCallback(
		async (values: RelatedDealParam) => {
			showLoader();
			const response = await create(values, deal.id);
			const responseId = response.data?.id;
			if (isSuccess(response) && responseId !== undefined) {
				cache.delete(propertyDealCacheKey(deal.propertyDealId));
				await router.push(PATH(responseId));
				notify({ content: "作成しました。", variant: "success" });
			} else {
				notify({ content: "エラーが発生しました。", variant: "error" });
			}
			hideLoader();
		},
		[
			showLoader,
			hideLoader,
			notify,
			router,
			cache,
			deal.id,
			deal.propertyDealId
		]
	);

	const onClickSubmit = React.useCallback(
		() =>
			handleSubmit(
				async (rawValues) => {
					const values = normalizeFields(rawValues);
					if (shouldConfirmShowTenant(values)) {
						setIsConfirmModalOpen(true);
						return;
					}
					await executeSave(values);
				},
				(errors) => {
					onInvalidScroll(errors);
				}
			)(),
		[handleSubmit, executeSave]
	);

	const handleConfirm = React.useCallback(async () => {
		setIsConfirmModalOpen(false);
		await executeSave(normalizeFields(getValues()));
	}, [executeSave, getValues]);

	return (
		<div className={Styles.DealEditorFooter}>
			<Loader />
			<Button
				className={Styles.SaveButton}
				disabled={isUploading}
				loading={isUploading}
				onClick={onClickSubmit}
				startIcon={<RiSaveLine className={Styles.Icon} />}
				variant="primary"
			>
				保存
			</Button>
			{isConfirmModalOpen ? (
				<ShowTenantConfirmModal
					onCancel={() => {
						setIsConfirmModalOpen(false);
					}}
					onConfirm={handleConfirm}
				/>
			) : null}
		</div>
	);
}
